import { Box, Divider, Paper, Table, TableBody, TableContainer, Typography } from '@mui/material';
import React, { ReactNode, useEffect, useState } from 'react';
import Loader from 'react-spinners/ClockLoader';
import Container from '../Container';
import Coluna from './Coluna';
import EdPopover from './EdPopover';
import EdTableHead from './EdTableHead';
import Filler from './Filler';
import Linha from './Linha';
import Paginacao from './Paginacao';
import TableInfo from './TableInfo';
import LegacyGrid from '../LegacyGrid';
import { legacyMakeStyles, useLocalStorage } from '../../helper';
import EdCheckbox from '../EdCheckBox';

const ALTURA_LINHA = 53;

type Props = {
  cabecalhoTabela: Coluna<any>[];
  linhas: any[];
  loading: boolean;
  chave: string;
  onRowClick?: (obj: any, index: number) => void;
  // adicionar?: () => void;
  stringTabelaVazia?: string;
  esconderToolbar?: boolean;
  compactar?: boolean;
  isRowSelected?: (obj: any, index: number) => boolean;
  elevation?: number;

  maxHeight?: number;

  // As props abaixo são NECESSÁRIAS para colocar paginação na tabela
  pagina?: number;
  linhasPorPagina?: number;
  totalLinhas?: number;
  mudarPagina?: (novaPagina: number) => void;
  colorSelected?: string;
  children?: ReactNode;
};

// function EdTabela<T>(props: PropsWithChildren<Props<T>>) {
const EdTabela: React.FC<Props> = (props) => {
  const {
    children,
    cabecalhoTabela,
    linhas,
    pagina,
    linhasPorPagina,
    totalLinhas,
    mudarPagina,
    onRowClick,
    // adicionar,
    stringTabelaVazia,
    chave,
    loading,
    esconderToolbar,
    compactar,
    isRowSelected,
    colorSelected,
    elevation,
    maxHeight
  } = props;
  const classes = useStyles();

  const [filterAnchor, setFilterAnchor] = useState<HTMLButtonElement | null>(null);
  const [columnsAnchor, setColumnsAnchor] = useState<HTMLButtonElement | null>();

  const [mostrarColunas, setMostrarColunas] = useLocalStorage<boolean[]>(`@EdTabela/${chave}`, []);

  useEffect(() => {
    if (mostrarColunas.length < cabecalhoTabela.length) {
      const verificaExibicao: boolean[] = [];
      for (let x = 0; x < cabecalhoTabela.length; x++) {
        verificaExibicao.push(cabecalhoTabela[x].show ?? true);
      }
      setMostrarColunas(verificaExibicao);
    }
  }, [cabecalhoTabela.length]);

  const esconderPaginacao =
    pagina === undefined ||
    linhasPorPagina === undefined ||
    totalLinhas === undefined ||
    mudarPagina === undefined ||
    totalLinhas === 0;

  let linhasVazias = 0;
  if (linhasPorPagina !== undefined) {
    linhasVazias = linhasPorPagina - Math.min(linhasPorPagina, linhas.length);
  }

  // const tabelaVazia = linhasVazias === linhasPorPagina;
  const tabelaVazia = linhas.length === 0;

  return (
    <Paper elevation={elevation !== undefined ? elevation : 2} sx={{ width: '100%' }}>
      <TableInfo
        setColumnsAnchor={setColumnsAnchor}
        setFilterAnchor={setFilterAnchor}
        showFilters={children ? true : false}
        hide={esconderToolbar}
      />

      <EdPopover
        open={filterAnchor ? true : false}
        anchorEl={filterAnchor}
        onClose={() => setFilterAnchor(null)}
        // style={{maxWidth: "700px"}}
      >
        <Container>
          <LegacyGrid container spacing={2}>
            <LegacyGrid item xs={12}>
              <h2>Filtrar</h2>
            </LegacyGrid>
            {children}
          </LegacyGrid>
        </Container>
      </EdPopover>

      <EdPopover
        open={columnsAnchor ? true : false}
        anchorEl={columnsAnchor}
        onClose={() => setColumnsAnchor(null)}
        // style={{maxWidth: "700px"}}
      >
        <Container>
          <LegacyGrid container>
            <LegacyGrid item xs={12}>
              <h2>Colunas</h2>
            </LegacyGrid>
            {mostrarColunas.length &&
              cabecalhoTabela.map((item, i) => (
                <LegacyGrid item xs={6} key={'coluna' + item.label + i}>
                  <EdCheckbox
                    label={item.label}
                    checked={mostrarColunas[i]}
                    onChange={() => {
                      const novoMostrarColunas = mostrarColunas.map((item, index) =>
                        index === i ? !item : item
                      );
                      setMostrarColunas(novoMostrarColunas);
                    }}
                  />
                </LegacyGrid>
              ))}
          </LegacyGrid>
        </Container>
      </EdPopover>

      <Divider light />

      {loading && (
        <Box component={'div'} style={{ height: (5 + 4) * ALTURA_LINHA }} sx={classes.root}>
          <Loader loading={loading} />
        </Box>
      )}

      {!loading && tabelaVazia && (
        <Box
          width={1}
          justifyContent="center"
          alignItems="center"
          display="flex"
          // style={{ height: ALTURA_LINHA * linhasVazias}}
          style={{ height: 100 }}
        >
          <Typography>
            {stringTabelaVazia ? stringTabelaVazia : 'Nenhum dado encontrado.'}
          </Typography>
        </Box>
      )}

      <TableContainer
        sx={classes.scrollContainer}
        style={{ overflowX: 'auto', minWidth: '100%', maxWidth: '100%', maxHeight: maxHeight }}
      >
        <Table
          sx={classes.table}
          aria-labelledby="Produtos"
          size={compactar ? 'small' : 'medium'}
          aria-label="produtos"
          stickyHeader
        >
          <EdTableHead
            chave={chave}
            hide={loading || tabelaVazia}
            colunas={cabecalhoTabela}
            mostrarColunas={mostrarColunas}
          />

          <TableBody>
            {!loading &&
              !tabelaVazia &&
              linhas.map((rowObject, index) => (
                <Linha
                  key={'tabela-row-' + index}
                  cabecalhoTabela={cabecalhoTabela}
                  mostrarColunas={mostrarColunas}
                  onRowClick={onRowClick}
                  rowObject={rowObject}
                  index={index}
                  // compactar={compactar}
                  selected={isRowSelected ? isRowSelected(rowObject, index) : false}
                  colorSelected={colorSelected}
                />
              ))}

            <Filler
              linhasVazias={linhasVazias}
              tabelaVazia={tabelaVazia}
              alturaLinha={ALTURA_LINHA}
            />
          </TableBody>
        </Table>
      </TableContainer>

      {!esconderPaginacao && (
        <Paginacao
          pagina={pagina}
          totalLinhas={totalLinhas}
          linhasPorPagina={linhasPorPagina}
          mudarPagina={mudarPagina}
        />
      )}
    </Paper>
  );
};

const useStyles = legacyMakeStyles({
  root: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center'
  },
  table: {
    // minWidth: 750,
  },
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: 20,
    width: 1
  },
  scrollContainer: {
    overflow: 'hidden',
    '&:hover': {
      overflow: 'auto'
    }
  }
});

export default EdTabela;
